/**
 * Twelve invented clients for Meridian Associates, each with 2-4 embedded projects.
 * Client names lifted from the mockups (manage-clients.html).
 *
 * Projects are also exported flat from `projects.ts`; this file filters that
 * flat list by `clientId` so there's exactly one source of truth per project.
 *
 * Dates derive from FROZEN_NOW.
 */

import type { Client, Project } from '@/types/entities';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';
import { PROJECTS } from './projects';

const DAY_MS = 24 * 60 * 60 * 1000;
const iso = (daysAgo: number): string => new Date(FROZEN_NOW_MS - daysAgo * DAY_MS).toISOString();

const projectsFor = (clientId: string): readonly Project[] =>
  PROJECTS.filter((p) => p.clientId === clientId);

export const CLIENTS: readonly Client[] = [
  {
    id: 'cli-meridian-capital',
    name: 'Meridian Capital',
    businessUnitId: 'bu-corporate',
    createdAt: iso(220),
    // Task 201 D5 — taxonomy metadata so the manage UIs render the new fields
    // with sample data in mockup mode.
    description: 'Long-time corporate restructuring and M&A advisory client.',
    keywords: ['restructuring', 'm&a', 'corporate', 'capital'],
    projects: projectsFor('cli-meridian-capital'),
    systemOwned: false,
  },
  {
    id: 'cli-techventure-labs',
    name: 'TechVenture Labs',
    businessUnitId: 'bu-ip',
    createdAt: iso(240),
    projects: projectsFor('cli-techventure-labs'),
    systemOwned: false,
  },
  {
    id: 'cli-apex-holdings',
    name: 'Apex Holdings',
    businessUnitId: 'bu-corporate',
    createdAt: iso(190),
    projects: projectsFor('cli-apex-holdings'),
    systemOwned: false,
  },
  {
    id: 'cli-pinnacle-financial',
    name: 'Pinnacle Financial',
    businessUnitId: 'bu-corporate',
    createdAt: iso(165),
    projects: projectsFor('cli-pinnacle-financial'),
    systemOwned: false,
  },
  {
    id: 'cli-anderson-trust',
    name: 'Anderson Family Trust',
    businessUnitId: 'bu-tax',
    createdAt: iso(260),
    projects: projectsFor('cli-anderson-trust'),
    systemOwned: false,
  },
  {
    id: 'cli-stellar-dynamics',
    name: 'Stellar Dynamics',
    businessUnitId: 'bu-litigation',
    createdAt: iso(140),
    projects: projectsFor('cli-stellar-dynamics'),
    systemOwned: false,
  },
  {
    id: 'cli-quantum-biotech',
    name: 'Quantum Biotech',
    businessUnitId: 'bu-ip',
    createdAt: iso(110),
    projects: projectsFor('cli-quantum-biotech'),
    systemOwned: false,
  },
  {
    id: 'cli-community-legal',
    name: 'Community Legal',
    businessUnitId: 'bu-litigation',
    createdAt: iso(300),
    projects: projectsFor('cli-community-legal'),
    systemOwned: false,
  },
  {
    id: 'cli-harmon-industries',
    name: 'Harmon Industries',
    businessUnitId: 'bu-litigation',
    createdAt: iso(90),
    projects: projectsFor('cli-harmon-industries'),
    systemOwned: false,
  },
  {
    id: 'cli-pacific-ventures',
    name: 'Pacific Ventures',
    businessUnitId: 'bu-corporate',
    createdAt: iso(70),
    projects: projectsFor('cli-pacific-ventures'),
    systemOwned: false,
  },
  {
    id: 'cli-northwood-partners',
    name: 'Northwood Partners',
    businessUnitId: 'bu-corporate',
    createdAt: iso(65),
    projects: projectsFor('cli-northwood-partners'),
    systemOwned: false,
  },
  {
    id: 'cli-crestview-holdings',
    name: 'Crestview Holdings',
    businessUnitId: 'bu-tax',
    createdAt: iso(45),
    projects: projectsFor('cli-crestview-holdings'),
    systemOwned: false,
  },
];
